<template>
  <div class="setupReminders">
    <mq-responsive target="mobile-tablet" class="setupReminders_illustration">
      <ac-image :src="illustrationMobile" aspect-ratio="720 / 566" object-fit="cover" alt="" />
      <button
        class="setupReminders_close -onImage"
        data-test="setup-close"
        aria-label="Закрыть"
        @click="onClose('cross_button')"
      >
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_24929_239821)">
          <path d="M14 12.3502L18.9502 7.40003C19.4057 6.94449 20.1443 6.94449 20.5998 7.40003C21.0554 7.85557 21.0554 8.59415 20.5998 9.0497L15.6496 13.9999L20.5998 18.95C21.0554 19.4056 21.0554 20.1442 20.5998 20.5997C20.1443 21.0552 19.4057 21.0552 18.9502 20.5997L14 15.6495L9.04982 20.5997C8.59427 21.0552 7.85569 21.0552 7.40015 20.5997C6.94461 20.1442 6.94461 19.4056 7.40015 18.95L12.3503 13.9999L7.40015 9.0497C6.94461 8.59415 6.94461 7.85557 7.40015 7.40003C7.85569 6.94449 8.59427 6.94449 9.04982 7.40003L14 12.3502Z" fill="white"/>
          </g>
          <defs>
          <clipPath id="clip0_24929_239821">
          <rect width="28" height="28" fill="white"/>
          </clipPath>
          </defs>
        </svg>
      </button>
      <div class="setupReminders_illustrationGradient"></div>
    </mq-responsive>

    <mq-responsive target="desktop" class="setupReminders_illustration -desktop">
      <ac-image :src="illustrationDesktop" aspect-ratio="766 / 582" object-fit="cover" alt="" />
      <button
        class="setupReminders_close -onImage"
        data-test="setup-close"
        aria-label="Закрыть"
        @click="onClose('cross_button')"
      >
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_24929_239821)">
          <path d="M14 12.3502L18.9502 7.40003C19.4057 6.94449 20.1443 6.94449 20.5998 7.40003C21.0554 7.85557 21.0554 8.59415 20.5998 9.0497L15.6496 13.9999L20.5998 18.95C21.0554 19.4056 21.0554 20.1442 20.5998 20.5997C20.1443 21.0552 19.4057 21.0552 18.9502 20.5997L14 15.6495L9.04982 20.5997C8.59427 21.0552 7.85569 21.0552 7.40015 20.5997C6.94461 20.1442 6.94461 19.4056 7.40015 18.95L12.3503 13.9999L7.40015 9.0497C6.94461 8.59415 6.94461 7.85557 7.40015 7.40003C7.85569 6.94449 8.59427 6.94449 9.04982 7.40003L14 12.3502Z" fill="white"/>
          </g>
          <defs>
          <clipPath id="clip0_24929_239821">
          <rect width="28" height="28" fill="white"/>
          </clipPath>
          </defs>
        </svg>
      </button>
      <div class="setupReminders_illustrationGradient"></div>
    </mq-responsive>

    <div class="setupReminders_content">
      <div class="setupReminders_head">
        <h2 class="setupReminders_title">{{ $t('postFirstDayModal.remindersTitle') }}</h2>
        <p class="setupReminders_desc">{{ $t('postFirstDayModal.remindersDesc') }}</p>
      </div>

      <div class="setupReminders_actions">
        <ac-button block data-test="setup-cta" @click="onCta">{{ $t('postFirstDayModal.remindersCta') }}</ac-button>
        <ac-button class="setupReminders_action -text" block variant="text" data-test="setup-later" @click="onClose('secondary_button')">
          {{ $t('postFirstDayModal.maybeLater') }}
        </ac-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { MqResponsive, useMq } from 'vue3-mq'

import PAGE_NAME from '@web/consts/PAGE_NAME'
import AcButton from '@ui-kit/components/AcButton.vue'
import AcImage from '@web/ui-kit/components/AcImage/index.vue'
import { postFirstDayModalCtaClicked, postFirstDayModalRedirectReminders } from '@web/services/Analytics/events'
import type { CloseMethod } from '@web/services/Analytics/events/postFirstDayModal'
import illustrationMobile from '@web/assets.v2/post-first-day/reminders-illustration-mobile.jpg'
import illustrationDesktop from '@web/assets.v2/post-first-day/reminders-illustration-desktop.jpg'

const emit = defineEmits<{ close: [method: CloseMethod]; cta: [] }>()
const router = useRouter()
const mq = useMq()

function platformView (): 'mobile_web' | 'desktop_web' {
  return mq.current === 'desktop' ? 'desktop_web' : 'mobile_web'
}

function onCta (): void {
  postFirstDayModalCtaClicked({ modalType: 'setup_reminders', platformView: platformView() })
  postFirstDayModalRedirectReminders({ platformView: platformView() })
  router.push({ name: PAGE_NAME.SETTINGS_REMINDERS_TRAINING, state: { autoEnableReminders: true } })
  emit('cta')
}

function onClose (method: CloseMethod): void {
  emit('close', method)
}
</script>

<style lang="scss" scoped>
@import '@web/assets/scss/textContsnts.scss';

.setupReminders {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 383px;
  max-height: 90dvh;
  background-color: var(--ac-color-white);
  border-radius: 12px 12px 0 0;
  overflow: hidden;

  @media (min-width: $screen-desktop) {
    border-radius: 12px;
  }

  &_illustration {
    position: relative;
    width: 100%;
  }

  &_illustrationGradient {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 89px;
    background: linear-gradient(180deg, rgba(var(--ac-color-rgb-white), 0) 0%, var(--ac-color-white) 100%);
  }

  &_close {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    line-height: 0;

    &.-onImage {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 1;
    }
  }

  &_content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 28px;
    width: 100%;
    padding: 8px 20px 22px;
    box-sizing: border-box;
  }

  &_head {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    text-align: center;
  }

  &_title {
    margin: 0;
    @extend .text-xl-medium;
    color: var(--ac-color-deep-navy-blue);

    @media (min-width: 1244px) {
      @include text-size(22px, 1.27, 500);
    }
  }

  &_desc {
    margin: 0;
    @extend .text-sm;
    color: var(--ac-color-dark-gray);
  }

  &_actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  &_action {
    &.-text {
      color: var(--ac-color-deep-liliac);
    }
  }
}
</style>
